<template>
	<app-title-bar :icon="['fas', 'fa-clipboard-check']" subtitle="Hire Desk" title="Stock Check">
		<div class="flex flex-col-reverse sm:flex-row items-center gap-4">
			<app-button to="/hire-desk" icon="fa-circle-left"> Back </app-button>
			<depots-dropdown
				v-if="depots"
				v-model="depots"
				size="large"
				required
				update-user
				@update:model-value="getData" />
		</div>
	</app-title-bar>

	<div class="sm:flex gap-4">
		<app-panel class="grow" title="Contract Details">
			<template #header>
				<div
					class="rounded px-2 py-1 focus:ring-2 flex items-center gap-2 bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50 text-gray-900">
					<app-checkbox
						id="withSecondary"
						v-model="withSecondary"
						label="Display other depots"
						name="withSecondary"
						wrapper=""
						:true-value="true"
						:false-value="false"
						@update:model-value="getData()" />
				</div>
			</template>

			<datatable
				v-model:server-options="params"
				:total
				:rows="params.rowsPerPage"
				:headers
				:value="items"
				:loading
				clickable
				@row-click="handleRowClick">
				<template #row-assigned="{ row }">
					<div class="flex items-center">
						<font-awesome-icon
							:icon="
								getAssignedIconClass(row.onHireProgress['stock_check_assignment'])
							"
							class="w-4 text-xl" />
					</div>
				</template>
			</datatable>
		</app-panel>
	</div>
</template>

<script lang="ts" setup>
	import DepotsDropdown from '@/components/partials/dropdowns/DepotsDropdown.vue'
	import { ref, onMounted, watch } from 'vue'
	import type { Header, Row } from '@/data/types/datatable'
	import ContractService from '@/services/contract'
	import useAuthStore from '@/stores/auth'
	import { useRouter } from 'vue-router'
	import useToasterStore from '@/stores/toaster'
	import { formatItemsDates } from '@/utils/date'
	import { useDatatable } from '@/composables/datatable.js'
	import { getAssignedIconClass } from '@/utils/contract'

	// Resources
	const api = new ContractService()
	const toaster = useToasterStore()
	const auth = useAuthStore()
	const router = useRouter()
	const { items, total, params } = useDatatable()

	// Data
	const depots = ref<string>('')
	const loading = ref(true)
	const withSecondary = ref(false)

	const headers: Header[] = [
		{ value: 'contract_number', text: 'Contract Number' },
		{ value: 'account_name', text: 'Account Name' },
		{ value: 'account', text: 'Account No' },
		{ value: 'delivery_date', text: 'Del Date' },
		{ value: 'site_name', text: 'Site Name' },
		{ value: 'site_address', text: 'Site Address' },
		{ value: 'assigned', text: 'Assigned' }
	]

	function handleRowClick(item: Row) {
		if (item && item.id) {
			router.push(`/hire-desk/stock-item/${item.contract_number}`)
		}
	}

	const getData = () => {
		loading.value = true
		const page = params.value.page
		const per_page = params.value.rowsPerPage

		if (depots.value) {
			api.getHireDeskArea('stock-check', depots.value, page, per_page, null, {
				withItemCount: true,
				withProgress: true
			})
				.then((res) => {
					items.value = formatItemsDates(res.data.data)
					if (res.data.meta && res.data.meta.paging) {
						total.value = Number(res.data.meta.paging.total)
					} else {
						total.value = 0
					}
					loading.value = false
				})
				.catch((error) => {
					console.log(error)
					toaster.error({ text: 'An error occurred retrieving contracts' })
					loading.value = false
				})
		} else {
			toaster.error({ text: 'Invalid depot' })
		}
	}

	watch(
		params,
		() => {
			getData()
		},
		{ deep: true }
	)

	onMounted(() => {
		if (auth.user && auth.user.current_depot) {
			depots.value = auth.user.current_depot
		}
		getData()
	})
</script>
